import * as Long from 'long';
import { im_proto } from '../../proto/index.js';
import { PushStatus, MuteState, StickTopState, FavoriteState, InfoKeys } from '../consts.js';
import { ConversationCoreInfo } from './ConversationCoreInfo.js';
import { ConversationSettingInfo } from './ConversationSettingInfo.js';
import moize from 'moize';
import { Logger } from '../metrics/logger.js';
import { ContextClassBase, ServiceIdentity } from '../service/RuntimeContext.js';
import { isTrueValue } from '../utils/tools.js';

/** 会话结构 */
class Conversation extends ContextClassBase {
    constructor() {
        super(...arguments);
        /** 是否是会话的成员 */
        this.isMember = true;
        this.__internal_pullCursor = Long.MAX_VALUE;
        /** @internal */
        this.cacheUnreadCountCalc = undefined;
        this.cacheUnreadCountWithWhiteListCalc = undefined;
    }
    /**
     * 会话类型
     * {@link ConversationType}
     */
    get type() {
        return this.bizType & 15;
    }
    set type(newType) {
        this.bizType = newType;
    }
    /** 会话所在的 InboxType */
    get inboxType() {
        return this.coreInfo.inboxType;
    }
    /**
     * 会话推拉模式 (陌生人), 熟人会话是 0
     * {@link StrangerPlugin}
     */
    get mode() {
        return 0;
    }
    /** 是否是陌生人会话 */
    get isStrangerConversation() {
        return this.mode !== 0;
    }
    /**
     * @deprecated 使用 pushStatus 判断更复杂的提醒状态
     * 会话是否被设为免打扰
     * {@link MuteState}
     */
    get isMuted() {
        // 兼容旧版
        if (this.pushStatus !== PushStatus.Unknown) {
            return this.pushStatus === PushStatus.Disable;
        }
        else {
            return this.settingInfo !== undefined && this.settingInfo.mute === MuteState.On;
        }
    }
    /**
     * 消息推送状态
     * {@link PushStatus}
     */
    get pushStatus() {
        var _a;
        return (_a = this.settingInfo.pushStatus) !== null && _a !== void 0 ? _a : PushStatus.Unknown;
    }
    /**
     * 低打扰模式关注配置
     * {@link PushStatus}
     */
    get weakMuteInfo() {
        return this.settingInfo.weakMuteInfo;
    }
    /**
     * 会话是否置顶
     * {@link StickTopState}
     */
    get isStickOnTop() {
        return this.settingInfo !== undefined && this.settingInfo.stickTop === StickTopState.On;
    }
    /**
     * 会话是否被收藏
     * {@link FavoriteState}
     */
    get isFavorite() {
        return this.settingInfo !== undefined && this.settingInfo.favor === FavoriteState.On;
    }
    /**
     * 获得会话的 ext, 注意这个是 core + setting(覆盖 core 同名 key), 需要获得单独的可以访问 {@link ConversationCoreInfo.ext} 和 {@link ConversationSettingInfo.ext}
     */
    get ext() {
        let ext = {};
        if (this.coreInfo && this.coreInfo.ext) {
            ext = Object.assign(ext, this.coreInfo.ext);
        }
        if (this.settingInfo && this.settingInfo.ext) {
            ext = Object.assign(ext, this.settingInfo.ext);
        }
        return ext;
    }
    // TODO: local ext
    /** @internal */
    get rankScore() {
        var _a, _b, _c, _d, _e;
        if (this.isStickOnTop) {
            // using setTopTime to calculate rank score
            // add the bias(8e15 < MAX_SAFE_INTEGER) to confirm it is always on top
            return ((_a = this.settingInfo.setTopTime) !== null && _a !== void 0 ? _a : Long.ZERO).add(8e15).toNumber();
        }
        else {
            // messages need to be poped, or the rank will not change
            return (_e = (_d = (_c = (_b = this.lastPopVisibleMessage) === null || _b === void 0 ? void 0 : _b.createdAt) === null || _c === void 0 ? void 0 : _c.getTime) === null || _d === void 0 ? void 0 : _d.call(_c)) !== null && _e !== void 0 ? _e : -1;
        }
    }
    /** 获得最后一条消息 */
    get lastMessage() {
        return this.filterLastMessage();
    }
    /**
     * 获得可见的最后一条消息
     * {@link Message.visible}
     */
    get lastVisibleMessage() {
        return this.filterLastMessage(msg => msg.visible);
    }
    /**
     * 获得需要顶出的最后一条消息
     * {@link Message.unpop}
     */
    get lastPopVisibleMessage() {
        return this.filterLastMessage(msg => !msg.unpop);
    }
    /** 获取第一条消息 */
    get firstMessage() {
        return this.filterFirstMessage();
    }
    /** 会话内已读的最近一条消息的序号 */
    get readIndex() {
        return this.settingInfo.readIndex;
    }
    get minIndex() {
        return this.settingInfo.minIndex;
    }
    /** 会话 Setting 版本 */
    get version() {
        return this.coreInfo.version;
    }
    /**
     * 获得会话当前的未读数
     * 注意是一个 getter
     */
    get unreadCount() {
        var _a, _b, _c;
        // 使用read index + update time做key, 缓存未读数计算结果
        if (this.cacheUnreadCountCalc === undefined) {
            this.cacheUnreadCountCalc = moize((_) => this.unreadMessageList.length, {
                maxSize: 1
            });
        }
        return this.cacheUnreadCountCalc(`${(_b = (_a = this.updatedAt) === null || _a === void 0 ? void 0 : _a.getTime) === null || _b === void 0 ? void 0 : _b.call(_a)}:${(_c = this.readIndex) === null || _c === void 0 ? void 0 : _c.toString()}`);
    }
    get unreadCountWithWhiteList() {
        var _a, _b, _c;
        // 使用read index + update time做key, 缓存未读数计算结果
        if (this.cacheUnreadCountWithWhiteListCalc === undefined) {
            this.cacheUnreadCountWithWhiteListCalc = moize((_) => this.unreadMessageListWithWhiteList.length, {
                maxSize: 1
            });
        }
        return this.cacheUnreadCountWithWhiteListCalc(`${(_b = (_a = this.updatedAt) === null || _a === void 0 ? void 0 : _a.getTime) === null || _b === void 0 ? void 0 : _b.call(_a)}:${(_c = this.readIndex) === null || _c === void 0 ? void 0 : _c.toString()}`);
    }
    forceRefreshUnreadCount() {
        var _a;
        const actualCount = this.unreadMessageList.length;
        if (actualCount !== this.unreadCount &&
            this.cacheUnreadCountCalc !== undefined &&
            this.cacheUnreadCountCalc.clear !== undefined) {
            Logger.ctxDebug(this.ctx, `force refresh unread count: ${this.id}, value: ${actualCount}`);
            (_a = this.cacheUnreadCountCalc) === null || _a === void 0 ? void 0 : _a.clear();
        }
    }
    get unreadMessageList() {
        // 没有readIndex时不计算, 理论上不应该出现
        if (!this.readIndex) {
            return [];
        }
        const msgs = this.resolve(ServiceIdentity.MessageManager).getList(this.id);
        return msgs.filter(msg => msg.indexInConversation.gt(this.readIndex) &&
            msg.indexInConversation.gt(this.minIndex) &&
            msg.increaseUnread);
    }
    get unreadMessageListWithWhiteList() {
        return this.unreadMessageList.filter(msg => {
            var _a, _b, _c;
            return ((_a = msg.ext) === null || _a === void 0 ? void 0 : _a[InfoKeys.MustNotify]) ||
                ((_b = this.settingInfo.weakMuteInfo.whiteUids) === null || _b === void 0 ? void 0 : _b.find(id => id.toString() === msg.sender)) ||
                ((_c = this.settingInfo.weakMuteInfo.whiteMsgTypes) === null || _c === void 0 ? void 0 : _c.find(id => id === msg.type));
        });
    }
    /**
     * 获得最后一条消息的创建时间
     * 暂时作为会话的显示更新时间
     * 如果没有消息, 返回0
     */
    get updatedAt() {
        return this.lastMessage ? this.lastMessage.createdAt : new Date(0);
    }
    get firstMessageIndex() {
        var _a, _b;
        return ((_b = (_a = this.filterFirstMessage(m => m.indexInConversation && m.indexInConversation.gt(0))) === null || _a === void 0 ? void 0 : _a.indexInConversation) !== null && _b !== void 0 ? _b : Long.ZERO);
    }
    get lastMessageIndex() {
        var _a, _b;
        return ((_b = (_a = this.filterLastMessage(m => m.indexInConversation && m.indexInConversation.gt(0))) === null || _a === void 0 ? void 0 : _a.indexInConversation) !== null && _b !== void 0 ? _b : Long.ZERO);
    }
    get lastMessageOrder() {
        var _a, _b;
        return ((_b = (_a = this.filterLastMessage(m => m.orderInConversation && m.orderInConversation.gt(0))) === null || _a === void 0 ? void 0 : _a.orderInConversation) !== null && _b !== void 0 ? _b : Long.ZERO);
    }
    /**
     * 返回当前会话的所有消息, map<string, Message>
     * Key 是消息的 clientId, 默认过滤不可见消息
     */
    getMessageList(filter = m => m.visible) {
        return this.resolve(ServiceIdentity.MessageManager).getList(this.id).filter(filter);
    }
    /**
     * 单聊会话的对方用户, 返回对方 uid
     * {@link toParticipant}
     */
    get toParticipantUserId() {
        if (this.type === im_proto.ConversationType.GROUP_CHAT) {
            return undefined;
        }
        const splitConId = this.id.split(':');
        return splitConId[2] === this.ctx.option.userId ? splitConId[3] : splitConId[2];
    }
    /**
     * 会话是否禁言
     */
    get isBlocked() {
        return isTrueValue(this.ext, InfoKeys.RelationIsMuted);
    }
    /**
     * 会话是否只禁言普通成员
     */
    get isBlockNormalOnly() {
        return this.isBlocked && isTrueValue(this.ext, InfoKeys.RelationNormalOnly);
    }
    /** @internal */
    static fromServerConversation(ctx, srv, logid) {
        const conv = new Conversation(ctx);
        conv.id = srv.conversation_id;
        conv.shortId = srv.conversation_short_id.toString();
        conv.type = srv.conversation_type;
        conv.ticket = srv.ticket;
        conv.participantCount = srv.participants_count;
        conv.isMember = srv.is_participant;
        conv.isOffline = false;
        conv.firstPageParticipant = srv.first_page_participants;
        conv.coreInfo = new ConversationCoreInfo(conv, srv.conversation_core_info);
        conv.settingInfo = new ConversationSettingInfo(conv, srv.conversation_setting_info);
        // duplicate key check
        const coreExtKey = Object.keys(conv.coreInfo.ext);
        for (const settingKey of Object.keys(conv.settingInfo.ext)) {
            if (coreExtKey.includes(settingKey)) {
                Logger.ctxDebug(conv.ctx, `conversation: ${conv.shortId} ext duplicate key: ${settingKey}`);
            }
        }
        if (logid !== undefined && conv.ctx.option.debug) {
            conv.coreInfo.ext[InfoKeys.LocalLogId] = logid;
        }
        return conv;
    }
    /** @internal */
    filterLastMessage(predicate) {
        const msgs = this.getMessageList(() => true);
        let lastMessage = null;
        if (msgs.length === 0) {
            return null;
        }
        if (predicate === undefined) {
            return msgs[msgs.length - 1];
        }
        for (let i = msgs.length - 1; i >= 0; i--) {
            if (predicate(msgs[i])) {
                lastMessage = msgs[i];
                break;
            }
        }
        return lastMessage;
    }
    /** @internal */
    filterFirstMessage(predicate) {
        const msgs = this.getMessageList(() => true);
        let firstMessage = null;
        if (msgs.length === 0) {
            return null;
        }
        if (predicate === undefined) {
            return msgs[0];
        }
        for (const msg of msgs) {
            if (predicate(msg)) {
                firstMessage = msg;
                break;
            }
        }
        return firstMessage;
    }
}

export { Conversation };
//# sourceMappingURL=Conversation.js.map
